/**
 * A meeting that never went through Calendly.
 *
 * Some of September's demos were arranged directly and never reached
 * Calendly, and meetings are still booked that way; until now there was
 * nowhere to put them, so the month's report was short by however many.
 *
 * It asks for what Calendly would have supplied and nothing more: who it
 * is with, what they wrote in the one free-text question, which of the
 * two meetings the workspace runs, when it starts, where it is held and
 * whose calendar it is on. The meeting decides whether this is a partner
 * or a customer — the same rule the sync applies to a Calendly booking.
 *
 * It does not ask whether they turned up. The clock says when — upcoming,
 * then happening now, then completed — and once the half hour is up the
 * row carries the two outcomes, Completed and No-show, so the one thing
 * the clock cannot see is said there. Asking twice, in two places, is
 * one place too many.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';

/* Calendly offers these two and nothing else, so neither does this. The
   name carries the customer type, so it is not asked for twice. */
const MEETINGS = ['30 Minute Meeting', 'B2B Meeting'];
const MINUTES = 30;

/* Where it was held. Every one of the 348 Calendly bookings says Google
   Meet, so that leads; the rest are here because a meeting arranged
   directly is exactly the one likely to have been held somewhere else. */
const PLACES = ['Google Meet', 'Zoom', 'Microsoft Teams', 'Phone call', 'In person'];

/* Dubai, because every other time on these screens is Dubai. An
   <input type="datetime-local"> hands back wall-clock with no zone, so the
   offset is added rather than left to the browser's own. */
const toDubaiIso = local => (local ? `${local}:00+04:00` : '');

/** Dubai wall clock, as the input wants it. */
const dubaiInput = ms => new Date(ms + 4 * 3600000).toISOString().slice(0, 16);

/** Now, rounded up to the next half hour. */
function defaultWhen() {
  const step = 30 * 60000;
  return dubaiInput(Math.ceil(Date.now() / step) * step);
}

/** "22 Sept 2026, 11:30 am", in Dubai. */
const dubaiText = ms => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true
}).format(new Date(ms)).toLowerCase();

/**
 * What the bookings already on the list say, for the two fields whose
 * right answer is "the same as all the others".
 *
 * Read rather than written down, so that a workspace renamed on Calendly
 * does not leave this sheet offering last year's name.
 */
function like(bookings, pick, fallback) {
  const seen = new Map();
  (bookings || []).forEach(b => {
    const v = pick(b);
    if (v) seen.set(v, (seen.get(v) || 0) + 1);
  });
  const best = [...seen.entries()].sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : fallback;
}

/**
 * The same sheet, adding one or correcting one.
 *
 * `booking` turns it into an edit: the fields arrive filled in and the
 * save patches instead of posting. One form rather than two, because a
 * meeting added by hand and a meeting corrected by hand are the same
 * facts, and two forms would drift apart within a month.
 */
export default function ManualMeeting({ onClose, onSaved, booking }) {
  const { toast, demos } = useApp();
  const editing = !!booking;
  const all = demos?.bookings || [];
  const [form, setForm] = useState(() => (editing
    ? {
      name: booking.invitee?.name || '',
      email: booking.invitee?.email || '',
      phone: booking.invitee?.phone || '',
      interest: booking.invitee?.interest || '',
      demoNotes: booking.session?.demoNotes || '',
      start: dubaiInput(Date.parse(booking.start)),
      eventName: MEETINGS.includes(booking.eventTypeName) ? booking.eventTypeName : MEETINGS[0],
      location: booking.where?.label || like(all, b => b.where?.label, PLACES[0]),
      host: booking.host?.name || like(all, b => b.host?.name, '')
    }
    : {
      name: '', email: '', phone: '', interest: '', demoNotes: '',
      start: defaultWhen(), eventName: MEETINGS[0],
      location: like(all, b => b.where?.label, PLACES[0]),
      host: like(all, b => b.host?.name, '')
    }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* The end time and the state the booking will be in, worked out here
     so both are visible before anybody commits to the time. */
  const startMs = form.start ? Date.parse(toDubaiIso(form.start)) : NaN;
  const ok = Number.isFinite(startMs);
  const endMs = ok ? startMs + MINUTES * 60000 : NaN;
  const now = Date.now();
  /* The three the clock can answer - see stateOf in domain/demos.js. A
     meeting whose half hour is up reads as completed, the same as a
     Calendly booking that passed without a no-show; the row carries the
     two outcomes afterwards, so it can be said to be a no-show there. */
  const state = !ok ? ''
    : endMs < now ? 'Completed — the row can be switched to a no-show'
      : startMs <= now ? 'Happening now'
        : 'Upcoming';

  const save = async () => {
    if (!form.name.trim()) { toast('Who is the meeting with?'); return; }
    if (!ok) { toast('When is the meeting?'); return; }
    setSaving(true);
    try {
      const body = { ...form, start: toDubaiIso(form.start) };
      if (editing) {
        await api.calendly.update(booking.session.id, body);
        toast(`${form.name.trim()} updated`);
      } else {
        await api.calendly.addManual(body);
        toast(`${form.name.trim()} added to the demo book`);
      }
      /* The date goes back with it: a meeting that already happened is not
         on a list showing what is upcoming, and the page widens its window
         rather than letting the row look like it vanished. */
      onSaved?.(toDubaiIso(form.start));
      onClose();
    } catch (e) {
      toast(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      icon="cal"
      tone="g-cyan"
      title={editing ? 'Edit the meeting' : 'Add a meeting'}
      onClose={onClose}
      onSave={saving ? undefined : save}
      saveLabel={saving ? 'Saving…' : editing ? 'Save the changes' : 'Add the meeting'}
    >
      {/* Exactly what the Calendly booking page asks an invitee for, and
          nothing else: of 348 real bookings, all of them carry a name, an
          email and a timezone, 242 a phone, and 27 an answer to the one
          question. None carries a business — Calendly never asks. */}
      <Group>Who it is with</Group>

      <Field label="Name" wide>
        <input autoFocus value={form.name} onChange={e => set('name', e.target.value)} />
      </Field>
      {/* The address is what matches them to an account, so it is worth
          getting right — without it the meeting counts as a demo but not
          as a demo with a customer. */}
      <Field label="Email" hint="Matches them to a client record">
        <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
      </Field>
      <Field label="Phone" hint="Matched on too, where the address differs">
        <input value={form.phone} onChange={e => set('phone', e.target.value)} />
      </Field>
      <Field label="Please share anything that will help prepare for our meeting" wide>
        <textarea rows={3} value={form.interest} onChange={e => set('interest', e.target.value)} />
      </Field>

      <Group>Which meeting, and when</Group>

      <Field label="Meeting" wide hint="B2B Meeting is a partner; 30 Minute Meeting is a customer">
        <OptionSelect value={form.eventName} onChange={v => set('eventName', v)} options={MEETINGS} />
      </Field>
      <Field label="Starts" hint="Dubai time">
        <input type="datetime-local" value={form.start} onChange={e => set('start', e.target.value)} />
      </Field>
      {/* Every meeting is half an hour, so the end is shown rather than
          asked for — one less thing to type and one less to get wrong. */}
      <Field label="Ends" hint={`Always ${MINUTES} minutes`}>
        <span className="fread">{ok ? dubaiText(endMs) : '—'}</span>
      </Field>
      {/* The two Calendly prints under the meeting name on every row.
          Without them a manual booking reads "—" where every other row
          says where it was and who ran it. */}
      <Field label="Where" hint="Shown under the meeting name, as it is for a Calendly booking">
        <OptionSelect value={form.location} onChange={v => set('location', v)} options={PLACES} />
      </Field>
      <Field label="Host" hint="Whose calendar it was on">
        <input value={form.host} onChange={e => set('host', e.target.value)} />
      </Field>
      {/* What the booking will read as the moment it is saved, so a date
          typed wrongly is caught here rather than on the list. */}
      <Field label="It will show as" wide hint="Worked out from the time you typed">
        <span className="fread">{state || '—'}</span>
      </Field>

      <Group>Anything worth keeping</Group>

      {/* Anything worth keeping about the meeting itself. It reads back on
          the booking's panel, under the question the invitee answered, and
          goes out with the demo in every export. */}
      <Field label="Notes" wide hint="Shown on the booking when it is opened">
        <textarea
          rows={4}
          value={form.demoNotes}
          onChange={e => set('demoNotes', e.target.value)}
          placeholder="What this meeting is about, how it was arranged, anything the next person should know."
        />
      </Field>
    </Sheet>
  );
}
